// Session key encoding utilities for remote storage
// Encodes session names to URL-safe keys to avoid path separator issues

/**
 * Generate 4-digit uppercase hex from SHA256 (trailing 16 bits)
 * Used as a checksum suffix to disambiguate encoded keys from literal names
 */
async function sha256ToHex4 (str: string): Promise<string> {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
    return new DataView(buf).getUint16(0, false).toString(16).padStart(4, '0').toUpperCase()
}

/**
 * Encode session name to URL-safe storage key with hash suffix
 * Uses UTF-8 safe encoding (handles Unicode characters)
 * Format: {urlSafeBase64}{4hexHash}
 */
export async function encodeSessionKey (name: string): Promise<string> {
    // UTF-8 safe base64 encoding
    const utf8Bytes = new TextEncoder().encode(name)
    const binary = String.fromCharCode(...utf8Bytes)
    const b64 = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    const hash = await sha256ToHex4(b64)
    return b64 + hash
}

/**
 * Decode storage key to session name
 * Returns null if not a valid encoded key (treat as literal name)
 */
export async function decodeSessionKey (key: string): Promise<string | null> {
    if (key.length < 5) return null  // Too short: minimum 1 char base64 + 4 hex

    const hash = key.slice(-4)
    const b64 = key.slice(0, -4)

    // Verify hash matches
    const expectedHash = await sha256ToHex4(b64)
    if (hash !== expectedHash) return null  // Hash mismatch: not encoded

    // Restore standard BASE64 (with padding) and decode
    try {
        const standardB64 = b64.replace(/-/g, '+').replace(/_/g, '/')
        const padding = (4 - (standardB64.length % 4)) % 4
        const binary = atob(standardB64 + '='.repeat(padding))
        // UTF-8 decode
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))
        return new TextDecoder().decode(bytes)
    } catch {
        return null
    }
}
